import React from "react";
import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ReactComponent as EthiquinNoData } from "../../../../../assets/images/Ethiquin/ethiquin_no_data.svg";
import { useReportContext } from "../../../ReportContext/useReportContext";
import { parseDateToISOString4 } from "../../../../../helpers/dateTransform";
import { UTCHint } from "../../../../MainComponents";
import "./Historical.scss";

/**
 * Historial de cambios de estatus, como sección plana de sidebar: solo el
 * título y el timeline, sin tarjeta ni acordeón y sin truncar — se muestra
 * completo y el scroll lo pone la página.
 */
export const Historical = ({ history }) => {
  // ---> Custom Hooks <--- //
  const [t] = useTranslation("report");
  const theme = useTheme();
  const { status_locales } = useReportContext();

  const isEmpty = Array.isArray(history) && history.length === 0;

  // History in chronological order, keeping the previous status of each change
  const sortedHistory = Array.isArray(history)
    ? [...history].sort((a, b) => new Date(a.assigned) - new Date(b.assigned))
    : [];

  // Cada cambio real de estatus abre un grupo; las entradas siguientes con el
  // mismo estatus (observaciones o actualizaciones) se anidan dentro de él, al
  // estilo de un historial de versiones, en vez de ser nodos de primer nivel.
  const groups = [];
  sortedHistory.forEach((item, idx) => {
    const fromStatus = idx > 0 ? sortedHistory[idx - 1].status : null;

    if (idx > 0 && fromStatus === item.status && groups.length > 0) {
      groups[groups.length - 1].children.push(item);
      return;
    }

    // El primer registro es el alta de la denuncia, no un cambio de estatus:
    // la aprueba un usuario interno de EthicsGlobal por un endpoint que no
    // registra al usuario, así que se narra como envío y no como cambio.
    groups.push({ ...item, fromStatus, children: [], isInitial: idx === 0 });
  });

  // Timeline completo, del cambio más reciente al más antiguo
  const timelineItems = [...groups].reverse();

  // Phrase describing the change of each group
  const actionText = (item) => {
    const toLabel = status_locales[item.status]?.label ?? item.status;
    if (item.isInitial) {
      return (
        <>
          <b>{item?.user?.name || "EthicsGlobal"}</b>{" "}
          {t("Care.sent_report_as")} <b>{toLabel}</b>
        </>
      );
    }
    if (item.fromStatus && item.fromStatus !== item.status) {
      const fromLabel =
        status_locales[item.fromStatus]?.label ?? item.fromStatus;
      return (
        <>
          {t("Care.changed_status_from")} <b>{fromLabel}</b> {t("Care.to")}{" "}
          <b>{toLabel}</b>
        </>
      );
    }
    return (
      <>
        {t("Care.changed_status_to")} <b>{toLabel}</b>
      </>
    );
  };

  return (
    <Box component="section">
      <Typography fontSize={15} fontWeight={600} sx={{ mb: 1.5 }}>
        {isEmpty
          ? t("Care.no_status_change_history")
          : t("Care.status_change_history")}
      </Typography>

      {isEmpty ? (
        <div className="no-data-etiquin">
          <EthiquinNoData width="125px" height="125px" />
        </div>
      ) : (
        <Stack mx={1}>
          {timelineItems.map((item, i, arr) => {
            const color =
              status_locales[item.status]?.color ??
              theme.palette.secondary.main;
            const isLast = i === arr.length - 1;
            return (
              <Stack key={i} direction="row" spacing={1.5}>
                {/* Dot & connector */}
                <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: alpha(color, 0.15),
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: color,
                      }}
                    />
                  </Box>
                  {!isLast && (
                    <Box
                      sx={{
                        flexGrow: 1,
                        width: "2px",
                        my: 0.5,
                        backgroundColor: theme.palette.blueGray50.main,
                      }}
                    />
                  )}
                </Stack>

                {/* Change description */}
                <Box pb={isLast ? 0 : 2.5} sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="body2">
                    {/* En el nodo inicial el actor ya viene en la frase.
                        Sin usuario registrado el actor es EthicsGlobal
                        (p. ej. la aprobación de borrador a nuevo) */}
                    {!item.isInitial && (
                      <b>{item?.user?.name || "EthicsGlobal"} </b>
                    )}
                    {actionText(item)}
                  </Typography>
                  {/* caption hereda ~16.5px por el htmlFontSize del tema;
                      la fecha debe leerse mas chica que el mensaje */}
                  <Typography
                    variant="caption"
                    color="secondary"
                    component="div"
                    fontSize={11}
                  >
                    {parseDateToISOString4(item.assigned, true)}
                    {item?.assigned && (
                      <UTCHint date={item.assigned} size={13} />
                    )}
                  </Typography>

                  {/* Observaciones del estatus, anidadas dentro del nodo
                      (la más reciente arriba, igual que el timeline) */}
                  {item.children.length > 0 && (
                    <Stack
                      spacing={1.25}
                      sx={{
                        mt: 1,
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.blueGray50.main, 0.5),
                        border: `1px solid ${theme.palette.blueGray50.main}`,
                      }}
                    >
                      {[...item.children].reverse().map((child, j) => (
                        <Stack key={j} direction="row" spacing={1}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              mt: "7px",
                              flexShrink: 0,
                              backgroundColor: alpha(color, 0.6),
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontSize={13}>
                              <b>{child?.user?.name || "EthicsGlobal"} </b>
                              {child.comment?.trim()
                                ? t("Care.added_observation")
                                : t("Care.updated_status_in")}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="secondary"
                              component="div"
                              fontSize={10.5}
                            >
                              {parseDateToISOString4(child.assigned, true)}
                              {child?.assigned && (
                                <UTCHint date={child.assigned} size={12} />
                              )}
                            </Typography>
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
